import { useEffect, useState, useCallback } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import useConfig from '@sdk/config/useConfig';
import gettext from '@sdk/lang/gettext';
import Picker from '@sdk/components/forms/Picker';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import ToggleGroup, {
  ToggleGroupOption,
} from '@sdk/components/forms/components/toggleGroup/ToggleGroup';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import useAuth from '@sdk/authentication/useAuth';
import { EmailNotificationFrequency } from '@sdk/authentication/types';

import {
  isNotificationPermissionDenied,
  isNotificationPermissionGranted,
  requestNotificationPermission,
} from '@web/common/utils/browserAPI/notification';

import type { ClientSettingsType, SetClientSettings } from '../../types';
import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import SmallHelpBlock from '../SmallHelpBlock';
import { WebNotificationType } from '../../types';
import { EMAIL_FREQUENCY_MODES, FIELD_WIDTH_420, MESSAGES_NOTIFY_MODES } from '../../constants';
import { useUserSettings } from '../../UserSettingsContext';

import { handleNotify } from './helpers/utils';

const StyledColumn = styled(Column)`
  align-items: flex-end;
`;

enum MessageNotify {
  messagesNotifyViaEmail = 'messagesNotifyViaEmail',
  messagesNotifyViaPush = 'messagesNotifyViaPush',
  messageNotifyViaBrowser = 'messageNotifyViaBrowser',
}

type Props = {
  clientSettings: ClientSettingsType;
  setClientSettings: SetClientSettings;
};

const MessageNotification: React.FC<React.PropsWithChildren<Props>> = ({
  clientSettings,
  setClientSettings,
}) => {
  const { currentUser } = useAuth();
  const { FEATURE_FLAGS } = useConfig();
  const { userSettings, onUserSettingsChange } = useUserSettings();
  const { messagesNotify, messagesNotifyViaEmail, messagesNotifyViaPush } = userSettings;

  const initializeNotificationOptions = (): ToggleGroupOption[] => {
    return [
      {
        name: MessageNotify.messagesNotifyViaEmail,
        label: 'Email',
        value: messagesNotifyViaEmail,
        disabled: false,
      },
      {
        name: MessageNotify.messagesNotifyViaPush,
        label: 'Mobile',
        value: messagesNotifyViaPush,
        disabled: !currentUser.canReceivePush,
      },
      {
        name: MessageNotify.messageNotifyViaBrowser,
        label: 'Web browser',
        value: clientSettings[WebNotificationType.notifications],
        disabled: isNotificationPermissionDenied(),
      },
    ];
  };

  const [messageToggleGroupOptions, setMessageToggleGroupOptions] = useState(() =>
    initializeNotificationOptions()
  );

  useEffect(() => {
    setMessageToggleGroupOptions(initializeNotificationOptions());
    onUserSettingsChange({
      messagesNotify: handleNotify(
        messagesNotify,
        messagesNotifyViaEmail,
        messagesNotifyViaPush,
        clientSettings[WebNotificationType.notifications]
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesNotifyViaEmail, messagesNotifyViaPush, clientSettings]);

  const onGrantedPermission = (): void => {
    setClientSettings({ [WebNotificationType.notifications]: true });
    const webToggleOptions = messageToggleGroupOptions.map((option) =>
      option.name === MessageNotify.messageNotifyViaBrowser ? { ...option, value: true } : option
    );

    setMessageToggleGroupOptions(webToggleOptions);
  };

  const onDeniedPermission = (): void => {
    setClientSettings({ [WebNotificationType.notifications]: false });
  };

  const onChangeHandler = async ({ name, value }) => {
    const updatedToggleOptions = messageToggleGroupOptions.map((option) =>
      option.name === name ? { ...option, value: value } : option
    );

    if (name === MessageNotify.messageNotifyViaBrowser) {
      if (!isNotificationPermissionGranted() && value) {
        requestNotificationPermission(onGrantedPermission, onDeniedPermission);
        return;
      }

      setClientSettings({ [WebNotificationType.notifications]: value });
      setMessageToggleGroupOptions(updatedToggleOptions);
      return;
    }

    setMessageToggleGroupOptions(updatedToggleOptions);
    onUserSettingsChange({ [name]: value });
  };

  const onEmailNotifyFrequencyUpdate = useCallback(
    (value: EmailNotificationFrequency) => {
      onUserSettingsChange({ messagesGroupEmailsByMinutesPeriod: value });
    },
    [onUserSettingsChange]
  );

  return (
    <SettingsSection pBottom={20}>
      <CollapsibleSettingsSection
        isActive={!!messagesNotify}
        headerItem={
          <SettingsSwitcherRow
            withSeparator
            value={!!messagesNotify}
            onChange={(value): void => onUserSettingsChange({ messagesNotify: value ? 1 : 0 })}
            subtitle={gettext(
              'Message notifications inform you of any new messages in conversations you are a part of. By default, these notifications are displayed as badges on the Messages tab.'
            )}
          />
        }
        contentItem={
          <>
            <Column pBottom={20}>
              <RowSpaceBetween>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Send me additional alerts when:')}
                </Text14>
                <Picker
                  options={MESSAGES_NOTIFY_MODES}
                  value={messagesNotify}
                  customWidth={FIELD_WIDTH_420}
                  onChange={(value: number): void =>
                    onUserSettingsChange({ messagesNotify: value })
                  }
                />
              </RowSpaceBetween>
            </Column>

            <Column pBottom={20}>
              <RowSpaceBetween>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Send notification via:')}
                </Text14>
                <StyledColumn>
                  <ToggleGroup onItemChange={onChangeHandler} options={messageToggleGroupOptions} />
                  {!currentUser.canReceivePush && <SmallHelpBlock />}
                </StyledColumn>
              </RowSpaceBetween>
            </Column>

            {FEATURE_FLAGS.GROUPED_EMAIL_NOTIFICATIONS_ENABLED && messagesNotifyViaEmail && (
              <Column pBottom={20}>
                <RowSpaceBetween>
                  <Text14 themeColor={ThemeColorType.gray}>
                    {gettext('Send email notifications:')}
                  </Text14>
                  <Picker
                    options={EMAIL_FREQUENCY_MODES}
                    value={
                      Number.isSafeInteger(userSettings.messagesGroupEmailsByMinutesPeriod)
                        ? userSettings.messagesGroupEmailsByMinutesPeriod
                        : 15
                    }
                    customWidth={FIELD_WIDTH_420}
                    onChange={onEmailNotifyFrequencyUpdate}
                  />
                </RowSpaceBetween>
              </Column>
            )}
          </>
        }
      />
    </SettingsSection>
  );
};

export default MessageNotification;
